import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { countTeamScoringRules } from "app/pages/sales-intelligence/domain/teams/utils/countTeamScoringRules";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";

import type { TeamListItem } from "../../types/teams.types";
import { buildTeamScoringSummaryLines } from "../TeamScoringTooltip/buildTeamScoringSummaryLines";
import { TeamScoringTooltipContent } from "../TeamScoringTooltip/TeamScoringTooltipContent";

import { navigateOnPlainClick } from "./navigateOnPlainClick";

const getRulesCountKey = createPluralHelperForI18nKey("si.management.teams.column.lead_scoring.count", true);

type TeamsTableScoringCellProps = {
    team: TeamListItem;
};

export const TeamsTableScoringCell = ({ team }: TeamsTableScoringCellProps) => {
    const { translate } = useTranslate();

    const rulesCount = countTeamScoringRules(team.leadScoringCriteria);

    const summaryLines = useMemo(
        () => buildTeamScoringSummaryLines(team.leadScoringCriteria),
        [team.leadScoringCriteria],
    );

    const scoringTabHref = siNavigator.createTeamDetailLink({ id: team.teamId, tab: "scoring" });
    const goToScoringTab = () => {
        void siNavigator.goToTeamDetailPage({ id: team.teamId, tab: "scoring" });
    };

    if (rulesCount === 0) {
        return (
            <Box
                as="a"
                display="inline-flex"
                textDecoration="none"
                href={scoringTabHref}
                onClick={(event) => navigateOnPlainClick(event, goToScoringTab)}
                data-automation={`teams-table-scoring-add-${team.teamId}`}
            >
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.blue["400"]}
                    whenHover={{ textDecoration: "underline" }}
                >
                    {`+ ${translate("si.management.teams.column.lead_scoring.add")}`}
                </Text>
            </Box>
        );
    }

    return (
        <Tooltip
            placement="top"
            content={
                <TeamScoringTooltipContent
                    lines={summaryLines}
                    dataAutomation={`teams-table-scoring-tooltip-${team.teamId}`}
                />
            }
        >
            <Box
                as="a"
                display="inline-flex"
                textDecoration="none"
                href={scoringTabHref}
                onClick={(event) => navigateOnPlainClick(event, goToScoringTab)}
                data-automation={`teams-table-scoring-${team.teamId}`}
            >
                <FiltersChip
                    text={translate(getRulesCountKey(rulesCount), { count: rulesCount })}
                    hideActionButton
                />
            </Box>
        </Tooltip>
    );
};
